import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { installerSaisieDecimale } from './lib/nombre'

// Gestionnaire global pour ignorer les erreurs des extensions Chrome/Firefox
const isExtensionError = (error: Error | string | null | undefined, source?: string): boolean => {
  const errorString = typeof error === 'string'
    ? error
    : (error?.message || '') + ' ' + (error?.stack || '');

  const sourceString = source || '';

  // Patterns d'erreurs d'extensions à ignorer
  const extensionPatterns = [
    'chrome-extension://',
    'moz-extension://',
    'insertBefore',
    'removeChild',
    'appendChild',
    'NotFoundError',
    'GotRequestId',
    'handleR',
    'injected.js',
    'tab.js',
    'Content Security Policy',
    'inline-speculation-rules',
    'React DevTools',
    // UUIDs typiques des extensions
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i,
  ];

  for (const pattern of extensionPatterns) {
    if (pattern instanceof RegExp) {
      if (pattern.test(errorString) || pattern.test(sourceString)) return true;
    } else {
      if (errorString.includes(pattern) || sourceString.includes(pattern)) return true;
    }
  }

  return false;
};

// Intercepter les erreurs globales - SILENCIEUSEMENT
window.onerror = (message, source, _lineno, _colno, error) => {
  if (isExtensionError(error || String(message), source as string)) {
    return true; // Bloque complètement l'erreur
  }
  return false;
};

// Intercepter les rejections de promesses - SILENCIEUSEMENT
window.onunhandledrejection = (event) => {
  if (isExtensionError(event.reason)) {
    event.preventDefault();
    return;
  }
};

// Intercepter console.error - SILENCIEUSEMENT
const originalConsoleError = console.error;
console.error = (...args) => {
  const errorString = args.map(a => String(a)).join(' ');
  if (isExtensionError(errorString)) {
    return; // Ignore silencieusement
  }
  originalConsoleError.apply(console, args);
};

// Intercepter console.warn pour React DevTools et autres
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
  const warnString = args.map(a => String(a)).join(' ');
  if (isExtensionError(warnString)) {
    return; // Ignore silencieusement
  }
  originalConsoleWarn.apply(console, args);
};

// Intercepter console.log pour les messages d'extensions
const originalConsoleLog = console.log;
console.log = (...args) => {
  const logString = args.map(a => String(a)).join(' ');
  if (logString.includes('React DevTools') || logString.includes('Download the')) {
    return; // Ignore silencieusement
  }
  originalConsoleLog.apply(console, args);
};

/**
 * Une page ouverte a cheval sur un deploiement continue de referencer les
 * anciens fichiers compiles (chaque nom porte un hash qui change a chaque
 * build). Des qu'elle a besoin d'un morceau charge a la demande — un ecran
 * pas encore visite dans cet onglet, une fenetre comme l'inventaire — le
 * navigateur va chercher ce nom exact, qui n'existe plus des qu'un nouveau
 * deploiement l'a remplace : 404, et l'ecran reste casse jusqu'a ce qu'on
 * pense a recharger soi-meme. Vite previent de cet echec precis par cet
 * evenement ; un seul rechargement automatique suffit a repartir sur les
 * bons fichiers, garde par sessionStorage pour ne jamais boucler si le
 * fichier manque pour une autre raison.
 */
const CLE_RECHARGEMENT_CHUNK = 'rechargement-apres-chunk-manquant';
window.addEventListener('vite:preloadError', () => {
  let dejaTente = true;
  try {
    dejaTente = sessionStorage.getItem(CLE_RECHARGEMENT_CHUNK) === '1';
    if (!dejaTente) sessionStorage.setItem(CLE_RECHARGEMENT_CHUNK, '1');
  } catch {
    dejaTente = true;
  }
  if (!dejaTente) window.location.reload();
});
// Une fois l'appli bien repartie, un futur deploiement doit pouvoir
// redeclencher ce meme rattrapage — sans ce nettoyage, un seul evenement
// dans la session bloquerait tout rechargement automatique ulterieur.
window.setTimeout(() => {
  try {
    sessionStorage.removeItem(CLE_RECHARGEMENT_CHUNK);
  } catch {
    // Navigation privee : sans memoire, rien a nettoyer.
  }
}, 10000);

installerSaisieDecimale();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
